import { isEditableFinancialDraft } from '../../shared/accounting-draft-eligibility';
import { Component, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Location, CommonModule } from '@angular/common';
import { MenuItem, MessageService, SelectItem } from "primeng/api";
import { SessionStorageService } from "../../../services/session-storage.service";
import { tap, forkJoin, Subject, debounceTime, switchMap, of, finalize, catchError } from "rxjs";
import { Customer } from "../../../customer/customer.model";
import { CustomerService } from "../../../customer/customer.service";
import { Company } from "../../../company/company.model";
import { ContractsService } from "../../../contracts/contracts.service";
import { DebitNote } from "../debit-note.model";
import { AccountsService } from "../../accounts.service";
import { DebitNoteLine } from "../debit-note-line.model";
import { Invoice } from "../../invoice/invoice.model";
import { Bill } from '../../payables/generated-bills/bill.model';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { SelectModule } from 'primeng/select';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { InputNumberModule } from 'primeng/inputnumber';
import { ToastModule } from "primeng/toast";
import { ProgressBarModule } from "primeng/progressbar";
import { DividerModule } from "primeng/divider";
import { FieldsetModule } from "primeng/fieldset";
import { InputGroupModule } from "primeng/inputgroup";
import { InputGroupAddonModule } from "primeng/inputgroupaddon";
import { IftaLabelModule } from "primeng/iftalabel";
import { InputTextModule } from 'primeng/inputtext';
import { DatePickerModule } from 'primeng/datepicker';
import { PermissionService } from '../../../security/permission.service';
import { app_permissions } from '../../../security/app-permissions';
import { PartyService } from '../../../party/party.service';
import { PartyType } from '../../../party/party-type.enum';
import { CrmClient, CrmClientCommunication } from '../../../crm/crm.model';
import { CrmClientContactPickerComponent } from '../../../shared/crm-client-contact-picker/crm-client-contact-picker.component';
import { CRM_RECEIVABLE_ROLES, CrmPartyContextService } from '../../../shared/crm-client-contact-picker/crm-party-context.service';
import { PageShellComponent } from '../../../shared/page-layout/page-shell.component';
import { PageHeroComponent } from '../../../shared/page-layout/page-hero.component';
import { PageMetricStripComponent } from '../../../shared/page-layout/page-metric-strip.component';
import { PageFormSectionComponent } from '../../../shared/page-layout/page-form-section.component';
import { PageDataPanelComponent } from '../../../shared/page-layout/page-data-panel.component';
import { PageMetric } from '../../../shared/page-layout/page-layout.models';
import { ChartOfAccountsService } from '../../../chart-of-accounts/chart-of-accounts.service';
import { AccountingTaxCode } from '../../accounting-tax-code.model';

@Component({
    selector: 'app-add-edit-debit-note',
    templateUrl: './add-edit-debit-note.component.html',
    styleUrls: ['./add-edit-debit-note.component.css'],
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        BreadcrumbModule,
        SelectModule,
        AutoCompleteModule,
        ButtonModule,
        TooltipModule,
        InputNumberModule,
        ToastModule,
        DatePickerModule,
        ProgressBarModule,
        DividerModule,
        FieldsetModule,
        InputGroupModule,
        InputGroupAddonModule,
        IftaLabelModule,
        InputTextModule,
        CrmClientContactPickerComponent,
        PageShellComponent,
        PageHeroComponent,
        PageMetricStripComponent,
        PageFormSectionComponent,
        PageDataPanelComponent
    ],
    providers: [
        MessageService,
        AccountsService,
        CustomerService,
        ContractsService,
        SessionStorageService,
        PartyService
    ]
})
export class AddEditDebitNoteComponent implements OnInit {

    breadcrumbItems: MenuItem[] = [];
    homeBreadcrumb: MenuItem = {};
    debitNote!: DebitNote;
    debitNoteForm!: FormGroup;
    editMode = false;
    debitNoteId!: number;
    isLoading: boolean = false;

    companies: SelectItem<Company>[] = [];
    customers: SelectItem<Customer>[] = [];
    bills: Bill[] = [];
    filteredBills: Bill[] = [];
    invoices: Invoice[] = [];
    filteredInvoices: Invoice[] = [];
    crmClients: CrmClient[] = [];
    crmContacts: CrmClientCommunication[] = [];
    selectedCrmClient?: CrmClient | null;
    selectedCrmContact?: CrmClientCommunication | null;
    private billSearchTerms = new Subject<string>();
    private invoiceSearchTerms = new Subject<string>();

    canSave = false;
    lineAccounts: SelectItem<number>[] = [];
    taxCodes: AccountingTaxCode[] = [];

    subtotal: number = 0;
    tax: number = 0;
    total: number = 0;
    currencyCode: string = 'SAR'; // Default currency

    get pageTitle(): string {
        return this.editMode ? 'Edit Debit Note' : 'New Debit Note';
    }

    get metrics(): PageMetric[] {
        return [
            { label: 'Subtotal', value: this.formatMoney(this.subtotal), hint: 'Lines before tax', tone: 'info' },
            { label: 'Estimated tax', value: this.formatMoney(this.tax), hint: 'Calculated from selected tax codes' },
            { label: 'Estimated total', value: this.formatMoney(this.total), hint: 'Includes estimated tax', tone: 'success' },
            { label: 'Lines', value: this.getDebitNoteLineControls?.length || 0, hint: 'Adjustment lines' }
        ];
    }

    get selectedClientName(): string {
        return this.selectedCrmClient?.companyName
            || this.debitNoteForm?.get('customer')?.value?.name
            || 'Client not selected';
    }

    constructor(
        private fb: FormBuilder,
        private route: ActivatedRoute,
        private location: Location,
        private messageService: MessageService,
        private debitNoteService: AccountsService,
        private customerService: CustomerService,
        private accountsService: AccountsService,
        private sessionStorageService: SessionStorageService,
        private router: Router,
        private permissionService: PermissionService,
        private chartAccounts: ChartOfAccountsService,
        private partyService: PartyService,
        private crmPartyContext: CrmPartyContextService
    ) {
    }

    ngOnInit(): void {
        this.breadcrumbItems = [
            { label: 'Accounts', routerLink: '/accounts' },
            { label: 'Debit Note', routerLink: '/accounts/enterprise/receivables/debit-note' },
            { label: this.editMode ? 'Edit Debit Note' : 'Add Debit Note' }
        ];
        this.homeBreadcrumb = { icon: 'pi pi-home', routerLink: '/' };


        this.initForm();
        this.loadAccountingOptions();
        this.initFilterLogic();
        this.route.params.subscribe(params => {
            this.debitNoteId = params['id'];
            this.editMode = !!this.debitNoteId;
            this.canSave = this.permissionService.hasPermission(this.editMode
                ? app_permissions.accounts.debit_note.edit : app_permissions.accounts.debit_note.create);
            this.loadData();
        });


    }

    loadData() {
        this.isLoading = true;

        const loadObservables = {
            companies: this.loadCompanies(),
            crmClients: this.crmPartyContext.loadClients(this.sessionStorageService.getCompany()?.id, CRM_RECEIVABLE_ROLES),
        };

        forkJoin(loadObservables)
            .pipe(
                tap(data => {
                    this.crmClients = data.crmClients ?? [];
                    if (this.editMode) {
                        this.loadDebitNote().subscribe();
                    } else {
                        if (this.companies.length > 0 && !Number(this.route.snapshot.queryParamMap.get('originalInvoiceId'))) {
                            this.onCompanyChange(this.companies[0].value);
                            this.debitNoteForm.patchValue({ company: this.companies[0].value });
                        }
                    }
                })
            )
            .subscribe({
                error: (error) => {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'An error occurred while loading data.' + error.message
                    });
                    this.isLoading = false;
                },
                complete: () => {
                    this.isLoading = false;
                    if (!this.editMode) this.restoreOriginalInvoiceFromRoute();
                }
            });
    }

    private restoreOriginalInvoiceFromRoute(): void {
        const id = Number(this.route.snapshot.queryParamMap.get('originalInvoiceId'));
        if (!id || this.editMode) return;
        this.isLoading = true;
        this.accountsService.getInvoiceById(id).pipe(finalize(() => this.isLoading = false)).subscribe({
            next: invoice => {
                if (!invoice?.invoiceId || invoice.owningCompany?.id !== this.sessionStorageService.getCompany()?.id) {
                    this.messageService.add({ severity: 'error', summary: 'Original invoice unavailable', detail: 'Choose an invoice belonging to the selected company.' });
                    return;
                }
                this.onInvoiceSelected(invoice);
            },
            error: () => this.messageService.add({ severity: 'error', summary: 'Original invoice unavailable', detail: 'Could not load the invoice for this adjustment note.' })
        });
    }

    loadCompanies() {
        return this.partyService.getParties(this.sessionStorageService.getCompany()?.id ?? 0, ['Company'], '', 0, 1000).pipe(
            tap(value => {
                this.companies = value.content.map(party => this.partyService.convertPartyToCompany(party)).map(company => ({
                    value: company,
                    label: company.name
                }));
            })
        );
    }

    onCompanyChange(company: Company) {
        if (company) {
            this.loadCustomersByCompany(company).subscribe();
            //this.billSearchTerms.next('');
            //this.invoiceSearchTerms.next('');
        } else {
            this.customers = [];
            this.debitNoteForm.patchValue({ customer: null });
            this.onCrmClientPicked(null);
            this.filteredBills = [];
            this.filteredInvoices = [];
            this.debitNoteForm.patchValue({ bill: null, invoice: null });
            this.currencyCode = 'SAR'; // Reset to default
        }
    }

    loadCustomersByCompany(company: Company) {
        return this.customerService.getCustomersByCompany(company.id).pipe(
            tap(customers => {
                this.customers = customers.map(customer => ({
                    value: customer,
                    label: customer.name
                }));
            })
        );
    }

    loadDebitNote() {
        return this.debitNoteService.getDebitNoteById(this.debitNoteId).pipe(
            tap(debitNote => {
                this.debitNote = debitNote;
                if (!isEditableFinancialDraft(debitNote, this.sessionStorageService.getCompany()?.id)) {
                    this.canSave = false;
                    this.debitNoteForm.disable();
                    this.messageService.add({ severity: 'error', summary: 'Draft unavailable', detail: 'Only unposted Draft notes for the selected company can be edited.' });
                    return;
                }
                this.debitNoteForm.patchValue(debitNote);
                this.debitNoteForm.get('reason')?.setValue(debitNote.reason || debitNote.manualReasonText || '');
                this.onCompanyChange(debitNote.company!);
                this.restoreCrmSelection(debitNote);
                this.debitNoteForm.setControl('debitNoteLines', this.fb.array(debitNote.debitNoteLines!
                    .map(line => this.createDebitNoteLineFormGroup(line))));
            })
        );
    }

    initForm() {
        this.debitNoteForm = this.fb.group({
            debitNoteID: [null],
            debitNoteNumber: [''],
            company: [null],
            customer: [null],
            crmClientId: [null],
            crmClientContactId: [null],
            bill: [null],
            invoice: [null],
            debitNoteDate: [new Date(), Validators.required],
            reason: [''],
            currencyCode: ['SAR', Validators.required],
            createdAt: [null],
            createdBy: [null],
            updatedAt: [null],
            updatedBy: [null],
            debitNoteLines: this.fb.array([this.createDebitNoteLineFormGroup()]),
        });

        this.debitNoteForm.valueChanges.subscribe(() => {
            this.calculateTotals();
        });
        this.debitNoteForm.get('currencyCode')?.valueChanges.subscribe(value => {
            const changed = (value || 'SAR').toUpperCase() !== this.currencyCode.toUpperCase();
            this.currencyCode = value || 'SAR';
            if (changed) this.getDebitNoteLineControls.controls.forEach(line => this.clearTaxSnapshot(line as FormGroup));
            this.calculateTotals();
        });
        this.calculateTotals();
    }

    createDebitNoteLineFormGroup(debitNoteLine?: DebitNoteLine): FormGroup {
        const group = this.fb.group({
            debitNoteLineID: [debitNoteLine?.debitNoteLineID ?? null],
            description: [debitNoteLine?.description ?? ''],
            amount: [debitNoteLine?.amount ?? 0, [Validators.required, Validators.min(0)]],
            accountId: [debitNoteLine?.accountId ?? null],
            taxCode: [debitNoteLine?.taxCode ?? null],
            taxRate: [debitNoteLine?.taxRate ?? null], taxCategory: [debitNoteLine?.taxCategory ?? null],
            taxableAmount: [debitNoteLine?.taxableAmount ?? null], exemptAmount: [debitNoteLine?.exemptAmount ?? null],
            zeroRatedAmount: [debitNoteLine?.zeroRatedAmount ?? null], taxAmount: [debitNoteLine?.taxAmount ?? null],
            totalAmount: [debitNoteLine?.totalAmount ?? null], exemptionReason: [debitNoteLine?.exemptionReason ?? null],
        });
        group.get('amount')?.valueChanges.subscribe(() => this.clearTaxSnapshot(group));
        group.get('taxCode')?.valueChanges.subscribe(() => {
            this.clearTaxSnapshot(group);
            group.patchValue({ exemptionReason: null }, { emitEvent: false });
        });
        return group;
    }

    private clearTaxSnapshot(group: FormGroup): void {
        group.patchValue({ taxRate: null, taxCategory: null, taxableAmount: null, exemptAmount: null,
            zeroRatedAmount: null, taxAmount: null, totalAmount: null }, { emitEvent: false });
    }

    get getDebitNoteLineControls(): FormArray {
        return this.debitNoteForm.get('debitNoteLines') as FormArray;
    }

    addDebitNoteLine() {
        this.getDebitNoteLineControls.push(this.createDebitNoteLineFormGroup());
    }

    removeDebitNoteLine(index: number) {
        this.getDebitNoteLineControls.removeAt(index);
    }

    saveDebitNote() {
        if (!this.canSave || (this.editMode && !isEditableFinancialDraft(this.debitNote, this.sessionStorageService.getCompany()?.id))) return;
        if (this.debitNoteForm.invalid) {
            this.messageService.add({
                severity: 'error',
                summary: 'Validation Error',
                detail: 'Please correct all form errors.'
            });
            return;
        }
        this.isLoading = true;
        const modifiedDebitNote = { ...(this.editMode ? this.debitNote : {}), ...this.debitNoteForm.getRawValue() } as DebitNote;
        modifiedDebitNote.crmClientId = this.selectedCrmClient?.id;
        modifiedDebitNote.crmClientContactId = this.selectedCrmContact?.id;
        modifiedDebitNote.owningCompanyId = this.sessionStorageService.getCompany()!.id;
        const userId = this.sessionStorageService.getCustomer()!.id

        if (this.editMode) {
            modifiedDebitNote.createdAt = this.debitNote.createdAt;
            modifiedDebitNote.createdBy = this.debitNote.createdBy;
            modifiedDebitNote.updatedAt = new Date();
            modifiedDebitNote.updatedBy = userId;
        } else {
            modifiedDebitNote.createdAt = new Date();
            modifiedDebitNote.createdBy = userId;
            modifiedDebitNote.updatedAt = new Date();
            modifiedDebitNote.updatedBy = userId;
        }

        modifiedDebitNote.debitNoteLines?.forEach(line => {
            line.createdAt = this.editMode ? line.createdAt : new Date();
            line.createdBy = this.editMode ? line.createdBy : userId;
            line.updatedAt = new Date();
            line.updatedBy = userId;
            line.debitNoteID = modifiedDebitNote.debitNoteID;
        });

        this.debitNoteService.createDebitNote(modifiedDebitNote).subscribe({
            next: (newDebitNote: DebitNote) => {
                this.isLoading = false;
                this.messageService.add({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Debit Note# ' + newDebitNote.debitNoteNumber + ' saved successfully.'
                });
                setTimeout(() => {
                    this.router.navigate(['/accounts/enterprise/receivables/debit-note']);
                }, 3000);
            },
            error: (error) => {
                this.isLoading = false;
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: error?.error?.message || 'Error saving debit note'
                });
            }
        });
    }

    cancel() {
        this.location.back();
    }

    filterBills(event: any) {
        this.billSearchTerms.next(event.query);
    }

    filterInvoices(event: any) {
        this.invoiceSearchTerms.next(event.query);
    }

    onCrmClientPicked(client: CrmClient | null): void {
        this.selectedCrmClient = client;
        this.selectedCrmContact = null;
        this.crmContacts = [];
        this.debitNoteForm.patchValue({ crmClientId: client?.id ?? null, crmClientContactId: null });
        this.patchLegacyPartyFromClient(client);
        if (client?.id) {
            this.crmPartyContext.loadContacts(client.id, this.sessionStorageService.getCompany()?.id).subscribe(contacts => {
                this.crmContacts = contacts;
                this.selectedCrmContact = this.crmPartyContext.preferredContact(contacts);
                this.debitNoteForm.patchValue({ crmClientContactId: this.selectedCrmContact?.id ?? null });
            });
        }
    }

    onCrmContactPicked(contact: CrmClientCommunication | null): void {
        this.selectedCrmContact = contact;
        this.debitNoteForm.patchValue({ crmClientContactId: contact?.id ?? null });
    }

    onInvoiceSelected(invoice: Invoice): void {
        if (!invoice) return;
        if (invoice.country?.currencyCode) this.debitNoteForm.patchValue({ currencyCode: invoice.country.currencyCode });
        if (invoice.mainParty?.id?.entityType === PartyType.Company) {
            const company = this.partyService.convertPartyToCompany(invoice.mainParty);
            if (!this.companies.some(option => option.value?.id === company.id)) {
                this.companies = [...this.companies, { label: company.name, value: company }];
            }
            this.debitNoteForm.patchValue({ company });
        }
        this.debitNoteForm.patchValue({
            invoice,
            originalInvoiceId: invoice.invoiceId,
            originalInvoiceNumber: invoice.invoiceNumber
        });
        this.restoreCrmSelection(invoice as any);
    }

    private restoreCrmSelection(record: { crmClientId?: number; crmClientContactId?: number; crmClient?: CrmClient | null }): void {
        const client = this.crmClients.find(item => item.id === record.crmClientId)
            || record.crmClient
            || null;
        if (!client) return;
        this.selectedCrmClient = client;
        this.debitNoteForm.patchValue({ crmClientId: client.id ?? null });
        this.patchLegacyPartyFromClient(client);
        this.crmPartyContext.loadContacts(client.id, this.sessionStorageService.getCompany()?.id).subscribe(contacts => {
            this.crmContacts = contacts;
            this.selectedCrmContact = this.crmPartyContext.preferredContact(contacts, record.crmClientContactId);
            this.debitNoteForm.patchValue({ crmClientContactId: this.selectedCrmContact?.id ?? null });
        });
    }

    private patchLegacyPartyFromClient(client?: CrmClient | null): void {
        if (!client) return;
        const customer = client.linkedCustomerId
            ? this.customers.find(option => option.value?.id === client.linkedCustomerId)?.value
            : null;
        const company = client.linkedCompanyId
            ? this.companies.find(option => option.value?.id === client.linkedCompanyId)?.value
            : null;
        this.debitNoteForm.patchValue({
            customer: customer ?? this.debitNoteForm.get('customer')?.value,
            company: company ?? this.debitNoteForm.get('company')?.value
        });
    }

    calculateTotals(): void {
        this.subtotal = this.getDebitNoteLineControls.controls.reduce((acc, line) => {
            const amount = line.get('amount')?.value || 0;
            return acc + amount;
        }, 0);
        this.tax = this.getDebitNoteLineControls.controls.reduce((sum, line) => {
            const code = this.taxCodes.find(item => item.taxCode === line.get('taxCode')?.value);
            return sum + (line.get('taxAmount')?.value ?? Number(line.get('amount')?.value || 0) * Number(code?.taxRate || 0) / 100);
        }, 0);
        this.total = this.subtotal + this.tax;
    }

    private loadAccountingOptions(): void {
        const companyId = this.sessionStorageService.getCompany()?.id;
        if (!companyId) return;
        forkJoin({ accounts: this.chartAccounts.getAllChartOfAccountsByCompany(companyId, false),
            taxCodes: this.accountsService.getAccountingTaxCodes(companyId) }).subscribe({
            next: result => {
                this.lineAccounts = result.accounts.filter(account => account.isActive && !account.deprecated
                    && ['revenue', 'income'].includes((account.internalType || '').toLowerCase()))
                    .map(account => ({ label: `${account.accountCode} - ${account.accountName}`, value: account.accountId }));
                this.taxCodes = result.taxCodes;
                this.calculateTotals();
            },
            error: () => this.messageService.add({ severity: 'error', summary: 'Accounting options unavailable',
                detail: 'Could not load accounts and tax codes.' })
        });
    }

    formatMoney(amount: number | undefined): string {
        return `${this.currencyCode || 'SAR'} ${Number(amount || 0).toLocaleString(undefined, { minimumFractionDigits: 4, maximumFractionDigits: 4 })}`;
    }

    private initFilterLogic() {
        this.billSearchTerms
            .pipe(
                debounceTime(300),
                switchMap((searchText: string) => {
                    const clientId = this.selectedCrmClient?.id;
                    const owningCompanyId = this.sessionStorageService.getCompany()!!.id;
                    return this.accountsService.getGeneratedBills(0, 20, owningCompanyId,
                        clientId ? [clientId] : undefined, undefined, undefined, undefined, undefined,
                        undefined, undefined, searchText).pipe(catchError(() => {
                            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Could not search bills.' });
                            return of({ items: [] });
                        }));
                }),
                tap(res => this.filteredBills = res.items)
            )
            .subscribe();

        this.invoiceSearchTerms
            .pipe(
                debounceTime(300),
                switchMap((searchText: string) => {
                    const clientId = this.selectedCrmClient?.id;
                    const owningCompanyId = this.sessionStorageService.getCompany()!!.id;
                    return this.accountsService.getGeneratedInvoices(0, 20, owningCompanyId,
                        clientId ? [clientId] : undefined, [], undefined, undefined, searchText).pipe(catchError(() => {
                            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Could not search invoices.' });
                            return of({ items: [] });
                        }));
                }),
                tap(res => this.filteredInvoices = res.items)
            )
            .subscribe();
    }
}
